import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Shuffle as ShuffleIcon,
  Casino as DiceIcon,
  Group as GroupIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import {
  Player,
  RandomGenerationResult,
  SwapAuditEntry,
  Division,
  PlayerNumberAssignment,
} from "../types";
import {
  generateRandomPlayerAssignments,
  generateMatchPattern,
} from "../utils/randomGenerator";
import { apiRequest } from "../config/api";
import SwapAuditTrailDisplay from "./SwapAuditTrailDisplay";

interface MatchWithNames {
  round: number;
  court: number;
  team1Players: string[];
  team2Players: string[];
}

interface RandomGeneratorDialogProps {
  open: boolean;
  players: Player[];
  courtCount?: number;
  division: Division;
  isTrial?: boolean;
  generationCount: number;
  onGenerate: () => void;
  onClose: () => void;
  onComplete: (result: RandomGenerationResult) => void;
}

export default function RandomGeneratorDialog({
  open,
  players,
  courtCount = 3,
  division,
  isTrial = false,
  generationCount,
  onGenerate,
  onClose,
  onComplete,
}: RandomGeneratorDialogProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<
    "ready" | "shuffling" | "complete"
  >("ready");
  const [shuffledPlayers, setShuffledPlayers] = useState<Player[]>([]);
  const [generationResult, setGenerationResult] =
    useState<RandomGenerationResult | null>(null);
  const [swapAuditTrail, setSwapAuditTrail] = useState<SwapAuditEntry | null>(null);
  const [loadingSwapPreview, setLoadingSwapPreview] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);

  const handleReset = () => {
    setCurrentPhase("ready");
    setShuffledPlayers([]);
    setGenerationResult(null);
    setSwapAuditTrail(null);
    setIsGenerating(false);
  };

  // Reset state when dialog opens/closes. generationCount is owned by the parent so it
  // keeps counting across Cancel + reopen instead of resetting to 0 here.
  useEffect(() => {
    if (!open) {
      handleReset();
    }
  }, [open]);

  // Safety check for player count
  useEffect(() => {
    if (players.length !== courtCount * 4 && currentPhase !== "ready") {
      console.warn("Player count changed during generation, resetting...");
      handleReset();
    }
  }, [players.length, currentPhase]);

  const startRandomGeneration = async () => {
    onGenerate();
    setIsGenerating(true);
    setCurrentPhase("shuffling");

    // Show shuffling animation
    await shufflePlayers();

    // Generate final result using original players array
    const result = generateRandomPlayerAssignments(players, courtCount);
    setGenerationResult(result);
    
    // Fetch swap preview from backend
    await fetchSwapPreview(result.assignments);
    
    setCurrentPhase("complete");
    setIsGenerating(false);
  };

  const shufflePlayers = async () => {
    const expectedPlayerCount = courtCount * 4;
    // Ensure we start with the correct number of players
    if (players.length !== expectedPlayerCount) {
      console.error(
        `Expected ${expectedPlayerCount} players, got:`,
        players.length
      );
      return;
    }

    // Create multiple shuffle iterations for visual effect
    const shuffleIterations = 8;
    const shuffleDuration = 300; // ms per iteration

    for (let i = 0; i < shuffleIterations; i++) {
      const shuffled = [...players].sort(() => Math.random() - 0.5);
      setShuffledPlayers(shuffled);

      // Rotate wheel visual effect
      if (wheelRef.current) {
        wheelRef.current.style.transform = `rotate(${i * 45}deg)`;
      }

      await new Promise((resolve) => setTimeout(resolve, shuffleDuration));
    }

    // Final shuffle for actual use - ensure exactly ${courtCount * 4} players
    const finalShuffled = [...players].sort(() => Math.random() - 0.5);
    setShuffledPlayers(finalShuffled);

    console.log("Final shuffled players count:", finalShuffled.length);
  };

  const fetchSwapPreview = async (assignments: any[]) => {
    try {
      setLoadingSwapPreview(true);
      const response = await apiRequest('/api/league-days/preview-swap', {
        method: 'POST',
        body: JSON.stringify({
          division,
          assignments,
          courtCount
        })
      });

      if (response.ok) {
        const auditTrail = await response.json();
        setSwapAuditTrail(auditTrail);
      } else {
        console.error('Failed to fetch swap preview');
      }
    } catch (error) {
      console.error('Error fetching swap preview:', error);
    } finally {
      setLoadingSwapPreview(false);
    }
  };

  // If the backend applied swaps to avoid repeat partnerships, use the final
  // swapped assignments everywhere in this dialog (preview, summary, exports)
  // so the displayed matches always match what actually gets saved.
  const getFinalAssignments = (): PlayerNumberAssignment[] => {
    if (!generationResult) return [];
    return swapAuditTrail && swapAuditTrail.swapsPerformed.length > 0
      ? swapAuditTrail.finalAssignments
      : generationResult.assignments;
  };

  const handleComplete = () => {
    if (generationResult) {
      onComplete({
        ...generationResult,
        assignments: getFinalAssignments(),
        generationCount,
      });
      onClose();
    }
  };

  const getPlayerNumber = (playerId: string): number | null => {
    if (!generationResult) return null;
    const assignment = getFinalAssignments().find(
      (a) => a.playerId === playerId
    );
    return assignment ? assignment.assignedNumber : null;
  };

  const getPlayerDisplayName = (player: Player): string => {
    const playerNumber = getPlayerNumber(player.id);
    return playerNumber ? `P${playerNumber} - ${player.name}` : player.name;
  };

  const generateCourtSchedule = (): MatchWithNames[] => {
    if (!generationResult) return [];

    // Generate dynamic court pattern based on court count
    const matchPattern = generateMatchPattern(
      getFinalAssignments(),
      courtCount,
      isTrial
    );

    // Convert the new format to the expected legacy format
    const matches: MatchWithNames[] = [];

    matchPattern.rounds.forEach((round) => {
      round.courts.forEach((court) => {
        matches.push({
          round: round.roundNumber,
          court: court.courtNumber,
          team1Players: [court.pair1.player1, court.pair1.player2],
          team2Players: [court.pair2.player1, court.pair2.player2],
        });
      });
    });

    return matches;
  };

  const printCourtSchedule = () => {
    const printContent = document.getElementById("printable-court-schedule");
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const date = new Date().toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Padel Court Schedule - ${date}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: black;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #1976d2;
              padding-bottom: 20px;
            }
            .round-section {
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .round-title {
              background-color: #1976d2;
              color: white;
              padding: 10px;
              text-align: center;
              font-weight: bold;
              font-size: 18px;
              border-radius: 5px;
              margin-bottom: 15px;
            }
            .courts-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
            }
            .court-card {
              border: 2px solid #1976d2;
              background-color: #f5f5f5;
              padding: 15px;
              text-align: center;
              border-radius: 8px;
            }
            .court-header {
              background-color: #ff6b35;
              color: white;
              padding: 8px;
              border-radius: 5px;
              font-weight: bold;
              margin-bottom: 15px;
            }
            .team {
              margin: 10px 0;
            }
            .team-1 {
              color: #2e7d32;
              font-weight: bold;
            }
            .team-2 {
              color: #1565c0;
              font-weight: bold;
            }
            .vs {
              color: #d32f2f;
              font-weight: bold;
              font-size: 16px;
              margin: 8px 0;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-style: italic;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 15px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🏟️ PADEL COURT SCHEDULE</h1>
            <h2>${date}</h2>
          </div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const getPhaseTitle = () => {
    switch (currentPhase) {
      case "ready":
        return "Ready to Generate Random Player Numbers";
      case "shuffling":
        return "Shuffling Players...";
      case "complete":
        return "Random Player Assignment Complete!";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: { xs: "95vh", sm: "90vh" },
          margin: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "auto" },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DiceIcon color="primary" />
          {getPhaseTitle()}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" paragraph>
          Watch the transparent random player number assignment! Players are
          shuffled randomly, then assigned numbers P1 through P${courtCount * 4}{" "}
          for the court schedule.
        </Typography>

        {players.length !== courtCount * 4 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "error.light" }}>
            <Typography variant="body1" color="error.dark">
              ⚠️ Exactly {courtCount * 4} players are required for random
              pairing. Currently selected: {players.length} players.
            </Typography>
          </Paper>
        )}

        {currentPhase === "ready" && players.length === courtCount * 4 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Selected Players ({players.length}/{courtCount * 4}):
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {players.map((player) => (
                  <Chip
                    key={player.id}
                    label={player.name}
                    variant="outlined"
                    size="medium"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {currentPhase === "shuffling" && (
          <Box textAlign="center">
            <Box
              ref={wheelRef}
              sx={{
                width: "300px",
                height: "300px",
                margin: "20px auto",
                borderRadius: "50%",
                border: "8px solid #1976d2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                transition: "transform 0.3s ease",
                background: "linear-gradient(45deg, #e3f2fd, #bbdefb)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                  width: "80%",
                  height: "80%",
                  placeItems: "center",
                }}
              >
                {shuffledPlayers.slice(0, 9).map((player, index) => (
                  <Chip
                    key={`${player.id}-${index}`}
                    label={player.name.split(" ")[0]}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      animation: "pulse 0.5s infinite alternate",
                    }}
                  />
                ))}
              </Box>
            </Box>
            <Typography variant="h6" color="primary">
              <TimerIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Randomly assigning player numbers P1-P{courtCount * 4}...
            </Typography>
          </Box>
        )}

        {currentPhase === "complete" && generationResult && (
          <Fade in timeout={500}>
            <Paper sx={{ p: 3, bgcolor: "success.light", textAlign: "center" }}>
              <Typography
                variant="h5"
                gutterBottom
                color="success.dark"
                sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }}
              >
                🎉 Random Player Assignment Complete! 🎉
              </Typography>
              <Chip
                label={
                  generationCount === 1
                    ? "Generated on the 1st attempt"
                    : `Generated on attempt #${generationCount}`
                }
                color={generationCount === 1 ? "success" : "warning"}
                variant="filled"
                sx={{ fontWeight: "bold", mb: 1 }}
              />
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                This count is saved with the league day for transparency.
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                All {courtCount * 4} players have been randomly assigned numbers
                P1 through P{courtCount * 4}.
              </Typography>

              {/* Swap Audit Trail Preview */}
              {loadingSwapPreview && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Checking for recent repeat partnerships...
                  </Typography>
                </Box>
              )}

              {swapAuditTrail && !loadingSwapPreview && (
                <Box sx={{ mt: 3, mb: 3 }}>
                  <SwapAuditTrailDisplay 
                    auditTrail={swapAuditTrail} 
                    compact={false}
                  />
                </Box>
              )}

              {/* Player Number Assignment Summary */}
              <Paper sx={{ p: 2, mt: 3, bgcolor: "info.light" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="info.dark"
                  sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                >
                  🎲 Player Number Assignments
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                  }}
                  gap={{ xs: 0.5, sm: 1 }}
                >
                  {getFinalAssignments()
                    .sort((a, b) => a.assignedNumber - b.assignedNumber)
                    .map((assignment) => (
                      <Chip
                        key={assignment.playerId}
                        label={`P${assignment.assignedNumber}: ${assignment.playerName}`}
                        color="primary"
                        variant="filled"
                        size="small"
                        sx={{
                          fontSize: "0.8rem",
                          bgcolor: "white",
                          color: "primary.main",
                          border: "1px solid",
                          borderColor: "primary.main",
                          fontWeight: "bold",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "white",
                          },
                        }}
                      />
                    ))}
                </Box>
              </Paper>

              {/* Printable Court Schedule */}
              <Paper
                sx={{
                  p: 3,
                  mt: 3,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  textAlign="center"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1.3rem", sm: "1.5rem" },
                  }}
                >
                  🏟️ PADEL COURT SCHEDULE
                </Typography>
                <Typography
                  variant="h6"
                  textAlign="center"
                  gutterBottom
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  {new Date().toLocaleDateString("en-GB", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>

                <Box
                  sx={{
                    bgcolor: "white",
                    p: 2,
                    borderRadius: 2,
                    mt: 2,
                    "@media print": {
                      bgcolor: "white !important",
                      color: "black !important",
                    },
                  }}
                  id="printable-court-schedule"
                >
                  {Array.from(
                    { length: isTrial ? 6 : courtCount === 1 ? 3 : 4 },
                    (_, i) => i + 1
                  ).map((round) => (
                    <Box key={round} sx={{ mb: 3 }} className="round-section">
                      <Typography
                        variant="h6"
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          p: { xs: 0.5, sm: 1 },
                          textAlign: "center",
                          fontWeight: "bold",
                          borderRadius: 1,
                          fontSize: { xs: "1.1rem", sm: "1.25rem" },
                        }}
                        className="round-title"
                      >
                        ROUND {round}{" "}
                        {isTrial
                          ? "(POINTS)"
                          : courtCount === 1
                          ? "(BEST OF 9)"
                          : round === 4
                          ? "(BEST OF 9)"
                          : "(BEST OF 7)"}
                      </Typography>

                      <Box
                        display="grid"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                          md: "repeat(3, 1fr)",
                        }}
                        gap={{ xs: 1, sm: 2 }}
                        mt={1}
                        className="courts-grid"
                      >
                        {generateCourtSchedule()
                          .filter((match) => match.round === round)
                          .map((match) => (
                            <Card
                              key={`${match.round}-${match.court}`}
                              sx={{
                                border: "2px solid #1976d2",
                                bgcolor: "#f5f5f5",
                              }}
                              className="court-card"
                            >
                              <CardContent
                                sx={{
                                  p: { xs: 1, sm: 2 },
                                  textAlign: "center",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    bgcolor: "secondary.main",
                                    color: "white",
                                    p: 0.5,
                                    borderRadius: 1,
                                    fontWeight: "bold",
                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                  }}
                                  className="court-header"
                                >
                                  COURT {match.court}
                                </Typography>

                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold", color: "green" }}
                                    className="team-1"
                                  >
                                    TEAM 1
                                  </Typography>
                                  {match.team1Players.map(
                                    (player: string, idx: number) => (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {player}
                                      </Typography>
                                    )
                                  )}

                                  <Typography
                                    variant="h6"
                                    sx={{
                                      my: 1,
                                      color: "red",
                                      fontWeight: "bold",
                                      fontSize: { xs: "1rem", sm: "1.25rem" },
                                    }}
                                    className="vs"
                                  >
                                    VS
                                  </Typography>

                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: "bold", color: "blue" }}
                                    className="team-2"
                                  >
                                    TEAM 2
                                  </Typography>
                                  {match.team2Players.map(
                                    (player: string, idx: number) => (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {player}
                                      </Typography>
                                    )
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          ))}
                      </Box>
                    </Box>
                  ))}

                  <Typography
                    variant="caption"
                    display="block"
                    textAlign="center"
                    mt={2}
                    sx={{
                      fontStyle: "italic",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    Generated: {new Date().toLocaleString("en-GB")} | Seed:{" "}
                    {generationResult.seed.substring(0, 15)}...
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  justifyContent="center"
                  gap={2}
                  mt={2}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={printCourtSchedule}
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    🖨️ Print Schedule
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                      const schedule = generateCourtSchedule();
                      const date = new Date().toLocaleDateString("en-GB", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                      });

                      // Helper function to generate court lines for a round
                      const generateCourtLines = (roundNumber: number) => {
                        const lines = [];
                        for (
                          let courtNum = 1;
                          courtNum <= courtCount;
                          courtNum++
                        ) {
                          const match = schedule.find(
                            (m) =>
                              m.round === roundNumber && m.court === courtNum
                          );
                          if (match) {
                            lines.push(
                              `🏟️ Court ${courtNum}: ${match.team1Players.join(
                                " + "
                              )} 🆚 ${match.team2Players.join(" + ")}`
                            );
                          }
                        }
                        return lines.join("\n");
                      };

                      const whatsappText = `🏟️ *PADEL COURT SCHEDULE* 🏟️
📅 ${date}

🎲 *RANDOM DRAW COMPLETE!*
${getFinalAssignments()
  .sort((a, b) => a.assignedNumber - b.assignedNumber)
  .map((a) => `P${a.assignedNumber} ${a.playerName}`)
  .join(" • ")}

📋 *MATCH SCHEDULE:*

${isTrial ? `
🔥 *ROUND 1* (Points)
${generateCourtLines(1)}

⚡ *ROUND 2* (Points)
${generateCourtLines(2)}

🎯 *ROUND 3* (Points)
${generateCourtLines(3)}

💪 *ROUND 4* (Points)
${generateCourtLines(4)}

🌟 *ROUND 5* (Points)
${generateCourtLines(5)}

🏆 *FINAL ROUND 6* (Points)
${generateCourtLines(6)}
` : `
🔥 *ROUND 1* (Best of ${courtCount === 1 ? "9" : "7"})
${generateCourtLines(1)}

⚡ *ROUND 2* (Best of ${courtCount === 1 ? "9" : "7"})
${generateCourtLines(2)}

🎯 *ROUND 3* (Best of ${courtCount === 1 ? "9" : "7"})
${generateCourtLines(3)}
${
  courtCount > 1
    ? `
🏆 *FINAL ROUND 4* (Best of 9)
${generateCourtLines(4)}`
    : ""
}
`}
                        

🎯 *Let's play!* Good luck everyone! 🎾`;

                      navigator.clipboard.writeText(whatsappText);
                      alert(
                        "🎉 Exciting schedule copied to clipboard for WhatsApp!"
                      );
                    }}
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    📱 Copy for WhatsApp
                  </Button>
                </Box>
              </Paper>
            </Paper>
          </Fade>
        )}

        {isGenerating && (
          <Box mt={2}>
            <LinearProgress color="primary" />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {currentPhase === "ready" && (
          <Button
            onClick={startRandomGeneration}
            variant="contained"
            startIcon={<DiceIcon />}
            disabled={players.length !== courtCount * 4}
            size="large"
          >
            Start Random Assignment
          </Button>
        )}
        {currentPhase === "complete" && (
          <>
            <Button onClick={handleReset} startIcon={<ShuffleIcon />}>
              Generate New Assignment
            </Button>
            <Button
              onClick={handleComplete}
              variant="contained"
              color="success"
              size="large"
            >
              Use This Assignment
            </Button>
          </>
        )}
      </DialogActions>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            100% { transform: scale(1.1); }
          }
          
          @media print {
            .MuiDialog-root {
              position: static !important;
            }
            .MuiDialog-container {
              position: static !important;
            }
            .MuiDialog-paper {
              box-shadow: none !important;
              margin: 0 !important;
              max-width: none !important;
              width: 100% !important;
              height: auto !important;
            }
            .MuiDialogTitle-root, .MuiDialogActions-root {
              display: none !important;
            }
            .MuiDialogContent-root {
              padding: 0 !important;
            }
            body * {
              visibility: hidden;
            }
            .MuiDialog-root, .MuiDialog-root * {
              visibility: visible;
            }
            .MuiDialog-root {
              position: absolute;
              left: 0;
              top: 0;
            }
          }
        `}
      </style>
    </Dialog>
  );
}
